import { useContext, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { GlobalContext } from "../GlobalContext"
import RedAlert from "../ui-elements/RedAlert"
import YellowAlert from "../ui-elements/YellowAlert"
import FormatQuestionAnswer from "./FormatQuestionAnswer"
import { getOrgImageUrl } from '../utils/getOrgImageUrl'
import FormatQuestionAnsweredBy from "./FormatQuestionAnsweredBy"
import { checkIfAnswerIsLocked } from "../utils/checkIfAnswerIsLocked"
import FormatQuestionTitle from "./FormatQuestionTitle"
import DisplayAverage from "./DisplayAverage"


type Props = {
  questionAnswer: ObjectStringKeyAnyValue
  checkPeriodId: string | undefined
  question: ObjectStringKeyAnyValue
  sortedQuestionsAndAnswers: Array<ObjectStringKeyAnyValue>
  answersForThisQuestion: Array<ObjectStringKeyAnyValue>
  answerNumber: number,
  parentQuestion: ObjectStringKeyAnyValue | null
  questionFormat?: 'compact' | 'full'
}

function QuestionSummaryDetail({
  questionAnswer,
  checkPeriodId,
  question,
  sortedQuestionsAndAnswers,
  answersForThisQuestion,
  answerNumber,
  parentQuestion,
  questionFormat
}: Props) {

  const {
    tableData,
    organisationImageCode,
    sendMessageToWebsocket,
    t
  } = useContext(GlobalContext)
  const navigate = useNavigate()

  const [isQuestionLocked, setIsQuestionLocked] = useState(false)
  const [lockedUntilText, setLockedUntilText] = useState('')
  const numberOfAlerts = questionAnswer && questionAnswer['AnswerAlerts'] ? Object.values(questionAnswer['AnswerAlerts']).filter((alert: any) => alert['actionToTake'] !== 'followUpQuestion').length : 0
  const numberOfNotes = questionAnswer && questionAnswer['AnswerNotes'] ? Object.values(questionAnswer['AnswerNotes']).length : 0

  // get coordinates if its a location answer
  const locationFromUser = questionAnswer && questionAnswer.Answer && questionAnswer.Answer.locationFromUser
  const locationFromGps = questionAnswer && questionAnswer.Answer && questionAnswer.Answer.locationFromGps
  let latFromUser = locationFromUser ? locationFromUser.lat : null
  let lngFromUser = locationFromUser ? locationFromUser.lng : null
  let latFromGps = locationFromGps ? locationFromGps.lat : null
  let lngFromGps = locationFromGps ? locationFromGps.lng : null
  let lat = latFromUser || latFromGps || null
  let lng = lngFromUser || lngFromGps || null


  let answerIcon = questionAnswer.Pending ? 'not-answered' : 'answered'
  if (questionAnswer.AnswerSkipped) {
    answerIcon = 'skipped'
  } else if (isQuestionLocked) {
    answerIcon = 'locked'
  }

  useEffect(() => {
    checkIfAnswerIsLocked(questionAnswer, setIsQuestionLocked, setLockedUntilText)
    const intervalId = setInterval(() => {
      checkIfAnswerIsLocked(questionAnswer, setIsQuestionLocked, setLockedUntilText)
    }, 1000)
    return () => clearInterval(intervalId)
  }, [])

  if (!question) return null


  return <div className={`w-full flex flex-row items-start`}>

    {(parentQuestion) &&
      <div className={`w-12 border-b-4 border-dotted border-white mt-2 mr-2`}></div>
    }

    <div
      className={`text-left w-full rounded flex flex-row  
        ${questionFormat === 'compact' ? ` bg-gray-300 ` : ` bg-white `}
        ${isQuestionLocked ? ` opacity-60 ` : ` hover:opacity-80 cursor-pointer `}
        `}
      onClick={() => {
        if (!isQuestionLocked && questionAnswer.Pending) {
          navigate(`/checklists/${checkPeriodId}/${questionAnswer.Id}`)
        }
        else if (!isQuestionLocked && !questionAnswer.Pending) {
          navigate(`/checklists/${checkPeriodId}/confirm/${questionAnswer.Id}`)
        }
      }}
    >


      {answerIcon === 'skipped' &&
        <div className={`w-16 bg-gray-600 text-white rounded-l py-4 px-2 flex flex-col justify-center items-center text-2xl font-bold`}>✕</div>}

      {answerIcon === 'answered' &&
        <div className={`w-16 bg-brandblue text-white rounded-l py-4 px-2 flex flex-col justify-center items-center text-2xl font-bold`}>✓</div>}

      {answerIcon === 'not-answered' &&
        <div className={`w-16 bg-red-400 text-white rounded-l py-4 px-2 flex flex-col justify-center items-center text-2xl font-bold`}>?</div>}

      {answerIcon === 'locked' &&
        <div className={`w-16 bg-gray-600 text-white rounded-l py-4 px-2 flex flex-col justify-center items-center text-2xl font-bold`}>🔐</div>}


      <div className={`w-full flex flex-row justify-between items-center`}>

        <div className={`w-full p-2 text-sm flex flex-col justify-center items-start gap-1`}>




          <FormatQuestionTitle question={question} classString={`text-lg font-righteous text-brandblue`} />

          {parentQuestion && <p className={`text-xs italic`}>
            {t('Dashboard.QuestionSummaryDetail.followUpFor')} "{parentQuestion.QuestionText}"
          </p>}


          <p className={`text-xs text-gray-600 italic`}>


            {answersForThisQuestion.length > 1 && `${t('Dashboard.QuestionSummaryDetail.repeatAnswer', { answerNumber: answerNumber, total: answersForThisQuestion.length })} `}


            {questionAnswer &&
              questionAnswer.repeatAnswerAfter &&
              questionAnswer.repeatAnswerAfter.type === 'timeDelay' &&
              `${t('Dashboard.QuestionSummaryDetail.repeatedAfter')}
              ${questionAnswer.repeatAnswerAfter.number < 60 ? 
                ` ${questionAnswer.repeatAnswerAfter.number} ${t('Time.minutes')} ` :
                ` ${Math.round(questionAnswer.repeatAnswerAfter.number / 60 * 10) / 10} ${t('Time.hours')} `}.`}

            <DisplayAverage
              question={question}
              classString=""
              checkPeriodId={checkPeriodId || ''}
            />


          </p>




          {lockedUntilText ? <p className={`text-xs text-gray-600 italic`}>
            {t('Dashboard.QuestionSummaryDetail.questionLocked')} {lockedUntilText}</p> : ''}





          {!questionAnswer && question.QuestionInstructions && <p className={`text-xs text-gray-600 italic`}><span className={`font-bold`}>{t('Dashboard.QuestionSummaryDetail.instructions')}</span> {question.QuestionInstructions} </p>}

          {questionAnswer.Pending && <p className={``}>{t('Dashboard.QuestionSummaryDetail.notYetAnswered')}</p>}


          {questionAnswer && !questionAnswer.Pending && <div className={`w-full flex flex-col items-start text-left gap-1`}>


            <p className={``}>
              <FormatQuestionAnswer
                questionAnswer={questionAnswer}
                isTemperature={question.AssetId ? true : false}
                question={question}
              />
            </p>

            <p className={`text-xs text-gray-600 italic`}>
              <span className={`font-bold`}>{t('Dashboard.QuestionSummaryDetail.answeredBy')}</span>
              <FormatQuestionAnsweredBy
                questionAnswer={questionAnswer}
              />
            </p>




            <div className={`w-full flex flex-row gap-2 items-start justify-start`}>
              {numberOfAlerts > 0 &&
                <RedAlert
                  icon={true}
                  fullwidth={false}
                  alignment={`left`}
                  size={`small`}
                >

                  {numberOfAlerts === 1 && t('Dashboard.QuestionSummaryDetail.oneAlert')}
                  {numberOfAlerts > 1 && t('Dashboard.QuestionSummaryDetail.multipleAlerts', { numberOfAlerts })}
                </RedAlert>}


              {numberOfNotes > 0 &&
                <YellowAlert
                  icon={true}
                  fullwidth={false}
                  alignment={`left`}
                  size={`small`}
                >

                  {numberOfNotes === 1 && t('Dashboard.QuestionSummaryDetail.oneNote')}
                  {numberOfNotes > 1 && t('Dashboard.QuestionSummaryDetail.multipleNotes', { numberOfNotes })}
                </YellowAlert>}

            </div>

          </div>}


        </div>

        {questionAnswer && questionAnswer.ImageFileName && organisationImageCode &&
          <img src={getOrgImageUrl(organisationImageCode, questionAnswer.ImageFileName)} alt=''
            className={`border border-gray-400 shadow rounded bg-gray-400 min-h-8 min-w-8 max-h-20 m-2`}
          />}


        {lat && lng && <div className={`w-48 h-32 border border-gray-400 bg-gray-100 shadow rounded m-2`}>
          <iframe
            width="100%"
            height="100%"
            frameBorder="0"
            scrolling="no"
            marginHeight={0}
            marginWidth={0}
            src={`https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`}
            title="Location Map"
          ></iframe>
        </div>}



      </div>
    </div>
  </div>


}


export default QuestionSummaryDetail